import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  Link,
  Switch,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  useConsentsRequests,
  useDocumentAcceptancesRequests,
  useLegalDocumentsRequests,
} from '../../../api/legal';
import { DocumentAcceptance, LegalDocument } from '../../../types/legal';
import { DOCUMENT_LABELS, sortDocuments } from '../../../components/ui/legalLinks/legalLinks.component';
import { DetailRow, DetailSection } from '../../../components/ui/detailView';
import { StatusChip } from '../../../components/ui/statusChip/statusChip.component';
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from '../../../consts';
import { formatDate } from '../../../utils';

/**
 * The permanent access point required by the terms: every published document,
 * what this user accepted and when, and the optional consents they can change.
 */
export const LegalDocuments = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { query, queryArchive } = useLegalDocumentsRequests();
  const { queryMine } = useDocumentAcceptancesRequests();
  const { query: queryConsents, update: updateConsent } = useConsentsRequests();

  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [archive, setArchive] = useState<LegalDocument[]>([]);
  const [acceptances, setAcceptances] = useState<DocumentAcceptance[]>([]);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const refreshConsents = useCallback(() => {
    queryConsents().then((state) => setMarketingConsent(Boolean(state?.consents?.MKT)));
  }, [queryConsents]);

  useEffect(() => {
    query().then((data) => setDocuments(sortDocuments(data)));
    queryArchive().then((data) => setArchive(data));
    queryMine().then((data) => setAcceptances(data));
    refreshConsents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptanceByDocumentId = useMemo(() => {
    const byId: Record<number, DocumentAcceptance> = {};
    acceptances.forEach((acceptance) => { byId[acceptance.document.id] = acceptance; });
    return byId;
  }, [acceptances]);

  const supersededByKind = useMemo(() => {
    const currentIds = new Set(documents.map((document) => document.id));
    const byKind: Record<string, LegalDocument[]> = {};
    archive
      .filter((document) => !currentIds.has(document.id))
      .forEach((document) => {
        byKind[document.kind] = [...(byKind[document.kind] || []), document];
      });
    return byKind;
  }, [archive, documents]);

  const handleMarketingToggle = async (granted: boolean) => {
    const { success } = await updateConsent('MKT', granted);
    if (!success) {
      enqueueSnackbar(t('Could not save your choice. Please try again.'), ERROR_SNACKBAR);
      return;
    }
    setMarketingConsent(granted);
    enqueueSnackbar(t('Your choice has been saved.'), SUCCESS_SNACKBAR);
  };

  return (
    <DetailSection title={t('Documents and consents')}>
      {documents.map((document) => {
        const acceptance = acceptanceByDocumentId[document.id];
        const superseded = supersededByKind[document.kind] || [];

        return (
          <DetailRow
            key={document.id}
            alignTop
            label={
              <Link href={document.url} target="_blank" rel="noopener noreferrer" underline="hover">
                {t(DOCUMENT_LABELS[document.kind])}
              </Link>
            }
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
              {acceptance ? (
                <StatusChip
                  color="success"
                  label={t('Accepted on {{date}}', { date: formatDate(acceptance.accepted_at) })}
                />
              ) : document.requires_action ? (
                <StatusChip color="warning" label={t('Not accepted yet')} />
              ) : (
                <StatusChip variant="outlined" label={t('Published for your information')} />
              )}
              <Typography variant="caption" color="text.secondary">
                {t('Version {{version}}, in force since {{date}}', {
                  version: document.version,
                  date: formatDate(document.effective_from),
                })}
              </Typography>

              {superseded.length > 0 && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 0 }}>
                    <Typography variant="caption">{t('Previous versions')}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 0, pt: 0 }}>
                    {superseded.map((previous) => (
                      <Link
                        key={previous.id}
                        href={previous.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        variant="body2"
                      >
                        {previous.version}
                      </Link>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          </DetailRow>
        );
      })}

      <Box sx={{ px: 2, py: 1.25 }}>
        <FormControlLabel
          control={
            <Switch
              checked={marketingConsent}
              onChange={(event) => handleMarketingToggle(event.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              {t('I want to receive commercial information about Copigo by email. Optional, can be withdrawn at any time.')}
            </Typography>
          }
        />
      </Box>
    </DetailSection>
  );
};
